'use client';

import React, { useEffect, useState } from 'react';
import Footer from '@/app/components/ui/Footer';
import Breadcrumb from '@/app/components/ui/Breadcrumb';
import ProjectCard from '@/app/components/projectCard/ProjectCard';
import ProjectFilters from '@/app/components/projectCard/ProjectFilters';
import { fetchProjectCategories } from '@/app/api/projects/fetchProjectCategories';
import { ProjectsDict } from '@/lib/dictionaries';
import ShimmerProjectCard from '@/app/components/projectCard/ShimmerProjectCard';
import Image from 'next/image';
import dynamic from "next/dynamic";
import SeoScript from '@/app/components/utils/SeoScript';
const NavBar = dynamic(() => import('@/app/components/navBar/NavBar'), { ssr: false });

interface ProjectsPageUIProps {
  projects: any[];
  locale: string;
  dict: ProjectsDict;
  footerDict: any;
  jsonLd:any

}

const ProjectsPageUI: React.FC<ProjectsPageUIProps> = ({
  projects,
  locale,
  dict,
  footerDict,
  jsonLd
}) => {
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [showCategories, setShowCategories] = useState(false);
  const [loading, setLoading] = useState(true); // ✅ loading = true au départ
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setError(false);
        setLoading(true); // démarre le shimmer
        const cats = await fetchProjectCategories(locale);
        setCategories(cats);
      } catch (e) {
        console.error("Erreur fetch catégories:", e);
        setError(true);
        setCategories([]);
      } finally {
        setLoading(false); // ✅ arrêt shimmer une fois fini
      }
    };
    loadCategories();
  }, [locale]);

  const validCategoryIds = categories.map((cat) => cat.id);
  const validProjects = (projects || []).filter(
    (project) =>
      Array.isArray(project.project_category) &&
      project.project_category.some((catId: number) => validCategoryIds.includes(catId))
  );

  const filteredProjects = selectedCategory
    ? validProjects.filter((project) => project.project_category.includes(selectedCategory))
    : validProjects;

  const usedCategoryIds = new Set(
    validProjects.flatMap((project) => project.project_category)
  );
  const filteredCategories = categories.filter((cat) => usedCategoryIds.has(cat.id));

  const handleResetFilters = () => {
    setSelectedCategory(null);
    setShowCategories(false);
  };

  const isEmpty = !error && !loading && filteredProjects.length === 0;

  return (
    <>
      <SeoScript jsonLd={JSON.stringify(jsonLd)} />
      <div className="font-sans">
        <section className="min-h-[35vh] bg-[#330052] text-white font-sans">
          <NavBar activeUnderline={dict.activeUnderlineTitle} />
          <div className="flex flex-col items-center justify-center text-center py-10 md:py-3 px-4">
            <h1 className="text-3xl md:text-5xl font-bold mb-4">{dict.title}</h1>
            <Breadcrumb homeLabel={dict.breadCrumbHome} currentLabel={dict.title} locale={locale} />
          </div>
        </section>

        <div className="min-h-screen bg-[#fff] py-12 px-4">
          <div className="max-w-7xl mx-auto">
            
            {error || isEmpty ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Image
                  src={error ? "/error.svg" : "/empty.svg"}
                  alt={error ? "Erreur lors du chargement" : "Aucun projet"}
                  width={272}
                  height={272}
                  className="mb-6"
                  decoding="async"
                  loading="lazy"
                />
                <p className="text-gray-600 text-lg font-medium text-center">
                  {error
                    ? "Une erreur est survenue lors du chargement des projets."
                    : dict.noProjects || "Aucun projet disponible pour le moment."}
                </p>
              </div>
            ) : (
              <>
                {!showCategories && filteredCategories.length > 0 && (
                  <div className="flex justify-end mb-8">
                    <button
                      className="cursor-pointer border rounded-full px-4 py-2 flex items-center gap-2 ml-4 transition-transform duration-150 shadow-sm bg-white"
                      style={{
                        color: '#330052',
                        borderColor: '#330052',
                        boxShadow: '0 1px 4px 0 rgba(51,0,82,0.08)',
                      }}
                      onClick={() => setShowCategories(true)}
                    >
                      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 20 20">
                        <path d="M3 6h14M6 10h8M9 14h2" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      {dict.filters}
                    </button>
                  </div>
                )}

                {showCategories && (
                  <ProjectFilters
                    categories={filteredCategories}
                    selectedCategory={selectedCategory}
                    onSelectCategory={setSelectedCategory}
                    onReset={handleResetFilters}
                    filtersLabel={dict.filters}
                    resetLabel={dict.reset}
                  />
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
                  {loading ? (
                    Array.from({ length: 5 }).map((_, idx) => (
                      <ShimmerProjectCard key={idx} />
                    ))
                  ) : (
                    filteredProjects.map((project) => (
                      <ProjectCard
                        key={project.id}
                        title={project.title?.rendered || ''}
                        image={project.acf?.project_image || '/default.jpg'}
                        technologies={
                          Array.isArray(project.acf?.project_technologies)
                            ? project.acf?.project_technologies
                            : (project.acf?.project_technologies
                                ? String(project.acf?.project_technologies).split(',').map((t: string) => t.trim())
                                : [])
                        }
                        audience={project.acf?.project_audience || ''}
                        seeDetailsLabel={dict.seeDetails}
                        locale={locale}
                        slug={project.slug}
                      />
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer dict={footerDict} locale={locale} />
    </>
  );
};

export default ProjectsPageUI;
